import {
  Box,
  Checkbox,
  Grid,
  H5,
  Inline,
  Input,
  P,
  Stack
} from "@adfenix/alexandria";
import { useFormContext } from "react-hook-form";
import { When } from "components/When/When";
import { AdTypes } from "./AdTypes";
import { RadioGroup } from "./util/RadioGroup";
import { ProgramService } from "@adfenix/types";
import { useDisabledFields } from "../useDisabledFields";
import Popover from "components/Popover";
import InfoCircle from "static/svg/info-circle";
import { useNonCustomTemplateContext } from "./util/CampaignTemplateContext";
import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { formMaxWidth } from "../bundle";

export const ChannelSettings = ({
  children
}: {
  children?: React.ReactNode;
}) => {
  const { t } = useTranslation();

  const disabledFields = useDisabledFields();
  const { register, setValue } = useFormContext<ProgramService.ProgramModel>();

  const { campaignTemplate, campaignKey, templateType } =
    useNonCustomTemplateContext();

  const defaultCustomCoordinates = campaignTemplate.staticCoordinates;

  const [coordinates, setCoordinates] = useState(
    defaultCustomCoordinates === null || defaultCustomCoordinates === undefined //to cover creating a new bundle
      ? false
      : true
  );

  const handleClick = (e: ChangeEvent<HTMLInputElement>): void => {
    setCoordinates(!coordinates);
    if (!e.target.checked) {
      // @ts-ignore
      setValue(`${campaignKey}.staticCoordinates`, null, {
        shouldDirty: true
      });
    }
  };

  return (
    <Stack spacing="l" style={{ maxWidth: formMaxWidth }}>
      <H5>{t("programBuilder.program.template.channel.settings.header")}</H5>
      <Grid columns={["1fr", "1fr 1fr"]} gap="l">
        {templateType === "meta" && <AdTypes campaignKey={campaignKey} />}

        <Stack spacing="xs">
          <P>
            {t("programBuilder.program.template.channel.settings.landingPage")}
          </P>

          <RadioGroup
            disabled={disabledFields(
              "bundleGroups.bundles.campaignTemplates.landingPage"
            )}
            data={[
              {
                value: "PropertyPage",
                label: t(
                  "programBuilder.program.template.channel.settings.propertyPage.label"
                )
              },
              {
                value: "ParsedAgentPage",
                label: t(
                  "programBuilder.program.template.channel.settings.parsedAgentPage.label"
                )
              },
              {
                value: "GetPreSaleAgentBoosterFromOfficeSetting",
                label: t(
                  "programBuilder.program.template.channel.settings.preSaleAgent.label"
                )
              },
              {
                value: "GetPostSaleAgentBoosterFromOfficeSetting",
                label: t(
                  "programBuilder.program.template.channel.settings.postSaleAgent.label"
                )
              },
              {
                value: "StaticUrl",
                label: t(
                  "programBuilder.program.template.channel.settings.statisUrl.label"
                )
              }
            ]}
            register={() => register(`${campaignKey}.landingPageOption`)}
          />

          <When truthy={campaignTemplate.landingPageOption === "StaticUrl"}>
            <Input
              readOnly={disabledFields(
                "bundleGroups.bundles.campaignTemplates.landingPageUrl"
              )}
              id={`${campaignKey}.staticLandingPageUrl`}
              label={t(
                "programBuilder.program.template.channel.settings.statisUrl.label"
              )}
              type="text"
              {...register(`${campaignKey}.staticLandingPageUrl`)}
            />
          </When>
          <Stack spacing={"s"}>
            <Checkbox
              readOnly={disabledFields(
                "bundleGroups.bundles.campaignTemplates.allowOrderWithCustomLandingPage"
              )}
              {...register(`${campaignKey}.allowOrderWithCustomLandingPage`)}
              label={t(
                "programBuilder.program.template.channel.settings.customLandingPage.label"
              )}
            />
          </Stack>
        </Stack>
      </Grid>
      <Grid columns={["1fr"]} gap="l">
        <P>Location</P>
        <Inline>
          <Checkbox
            readOnly={disabledFields(
              "bundleGroups.bundles.campaignTemplates.allowCustomLocation"
            )}
            {...register(`${campaignKey}.allowCustomLocation`)}
            label={t(
              "programBuilder.program.template.channel.settings.customLocation.label"
            )}
          />
          <Popover
            style={{
              display: "flex",
              width: "23px",
              justifyContent: "center"
            }}
            trigger={<InfoCircle />}
          >
            {t(
              "programBuilder.program.template.channel.settings.customLocation.trigger"
            )}
          </Popover>
        </Inline>
        <Box display="flex" flexDirection="row">
          <Checkbox
            readOnly={disabledFields(
              "bundleGroups.bundles.campaignTemplates.customCoordinates"
            )}
            id={`${campaignKey}.customCoordinates}`}
            checked={coordinates}
            name="customCoordinates"
            label={t(
              "programBuilder.program.template.channel.settings.statisCoordinates.label"
            )}
            onChange={e => handleClick(e)}
          />
          <Popover
            style={{
              display: "flex",
              width: "23px",
              justifyContent: "center",
              marginLeft: "10px"
            }}
            trigger={<InfoCircle />}
          >
            {t(
              "programBuilder.program.template.channel.settings.statisCoordinates.popover"
            )}
          </Popover>
        </Box>
      </Grid>

      <Grid columns={["1fr", "1fr 1fr"]} gap="l">
        {coordinates && (
          <>
            <Input
              type="number"
              step="any"
              placeholder={t(
                "programBuilder.program.template.channel.settings.latitude.placeholder"
              )}
              {...register(`${campaignKey}.staticCoordinates.latitude`, {
                required: t(
                  "programBuilder.program.template.channel.settings.latitude.required"
                ),

                min: {
                  value: -90,
                  message:
                    "programBuilder.program.template.channel.settings.latitude.min"
                },
                max: {
                  value: 90,
                  message:
                    "programBuilder.program.template.channel.settings.latitude.max"
                }
              })}
              readOnly={disabledFields(
                "bundleGroups.bundles.campaignTemplates.customCoordinates.latitude"
              )}
            />

            <Input
              type="number"
              step="any"
              placeholder={t(
                "programBuilder.program.template.channel.settings.longitude.placeholder"
              )}
              {...register(`${campaignKey}.staticCoordinates.longitude`, {
                required: t(
                  "programBuilder.program.template.channel.settings.longitude.required"
                ),

                min: {
                  value: -180,
                  message:
                    "programBuilder.program.template.channel.settings.longitude.min"
                },
                max: {
                  value: 180,
                  message:
                    "programBuilder.program.template.channel.settings.longitude.max"
                }
              })}
              readOnly={disabledFields(
                "bundleGroups.bundles.campaignTemplates.customCoordinates.longitude"
              )}
            />
          </>
        )}
      </Grid>

      {children}
    </Stack>
  );
};
